// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { omitBy, isNil, uniqBy, isEmpty } from "lodash";
import { useRecoilState } from "recoil";
import moment from "moment";

// STYLE
import "./../customer-management.scss";
import "./customer-profile-list.scss";

// ANT COMPONENTS
import {
  ActionMenu,
  AlertBox,
  BasicModal,
  Button,
  DataTable,
  SelectInput,
  TimePicker,
} from "@life-line-lab/components-react";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Input, Row, Col, Spin } from "antd";

// SHARED COMPONENT
import DataTableFilter from "Components/shared/data-table-filter";

// TYPES
import { CustomerType, OrgType } from "../../../types/customer-form.types";
import { Column, OrgList, Sorting } from "../../../types/common.types";

// JSON LIST
import countryList from "../../../data/country.json";

// SERVICE
import { CustomerApprovalInput } from "Services/customer.service";

// HELPERS
import { encode } from "Helpers/enc";

// ATOM
import { customerAtom, CustomerAtomType } from "Recoil/customer.atom";

// HOOKS
import useSession from "Hooks/session.hook";
import useAccessControlList from "Hooks/access-control.hook";
import { FilterSelectionProps } from "Components/shared/data-table-filter/data-table-filter";
import { ToTitleCase } from "Helpers/to-title-case";

import normalize from "Utils/normalizer";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";

interface Filters {
  [key: string]: string[] | string | boolean | null;
}

interface CustomerProfileListComponentProps {
  forceRender?: boolean;
  extraFilters?: Filters;
  onPaginationChange?: (page?: string) => void;
  onFilter: (filters: { [key: string]: string | boolean }) => void;
  onSearch?: (text: string) => void;
  onSort: (sort: string) => void;
  onViewDetails: (orgID: string) => void;
  onSubmitApprovalStatus: (data: CustomerApprovalInput, callBack) => void;
  onDeleteCustomer: (orgID: string, tempDeletedReason: string) => void;
  onFetchCustomerList: () => void;
  onSubmitOperationalHours: (operationalHour: any, orgIDs: any) => void;
  tab: number;
  list: any[];
  sorting: Sorting;
  isReadonly: boolean;
  showCompanyColumn: boolean;
  logisticList: OrgList[];
  companyList: OrgList[];
  isLoading: boolean;
}

const CustomerProfileListComponent: React.FC<CustomerProfileListComponentProps> = ({
  forceRender,
  extraFilters,
  onPaginationChange,
  tab,
  onFilter,
  onSearch,
  onViewDetails,
  onSubmitApprovalStatus,
  onDeleteCustomer,
  onFetchCustomerList,
  onSubmitOperationalHours,
  onSort,
  list,
  sorting,
  isReadonly = false,
  showCompanyColumn,
  logisticList,
  companyList,
  isLoading = false,
}) => {
  /*TODO: Refactor: There are multiple modals in this component that is not separated, I'm assuming
            because it's not shared with anywhere else therefore it is here. But feel free to separate these modals
            to make this page shorter
  */
  const history = useHistory();
  const { t } = useTranslation();
  const { roleTypes } = useSession();

  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Customer_Management",
  });

  const showCompanyListFilter = permission.rights.includes("COMPANY_LIST_FILTER");
  const showLogisticProviderListFilter = permission.rights.includes(
    "LOGISTIC_PROVIDER_LIST_FILTER",
  );

  const initialModal = {
    open: false,
    type: "approve",
    record: {},
  };

  const initialError = {};
  const initialInput = { reason: "" };

  const [modal, setModal] = useState<any>(initialModal);
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [globalFilterState, setGlobalFilterState] = useRecoilState(GlobalFilterAtom);
  const [filter, setFilter] = useState<{ [key: string]: any }>({
    customerType: "",
    companyOrgID: "",
    logisticProviderOrgID: "",
    ancestorOrgIDs: globalFilterState?.selectedOrgIDs?.length
      ? globalFilterState?.selectedOrgIDs
      : null,
  });
  const [customerStatus] = useRecoilState<CustomerAtomType>(customerAtom);

  useEffect(() => {
    if (customerStatus.delete === "success") {
      resetModalState();
    }
    if (customerStatus.restore === "success") {
      resetModalState();
    }
    if (customerStatus.reject === "success") {
      resetModalState();
    }
  }, [customerStatus]);

  useEffect(() => {
    onFilter(filter);
  }, [filter]);

  useEffect(() => {
    if (!isEmpty(globalFilterState) && globalFilterState?.selectedOrgIDs?.length) {
      setFilter({ ...filter, ancestorOrgIDs: globalFilterState?.selectedOrgIDs });
    }
  }, [globalFilterState]);

  const generateSortableTitle = ({ title, sortBy }: { title: string; sortBy: string }) => {
    return (
      <div className="sortable-title" onClick={() => onSort(sortBy)}>
        <span className="mr-3">{title}</span>

        {sorting.sortBy === sortBy && sorting.mode === "ASC" && (
          <span className="icon">
            <CaretUpOutlined />
          </span>
        )}

        {sorting.sortBy === sortBy && sorting.mode === "DESC" && (
          <span className="icon">
            <CaretDownOutlined />
          </span>
        )}

        {sorting.sortBy !== sortBy && (
          <span className="icon">
            <CaretUpOutlined />
            <CaretDownOutlined />
          </span>
        )}
      </div>
    );
  };

  const dataTableFilters: FilterSelectionProps[] = [
    {
      key: "Filter",
      label: t("placeholder.filterByCustomerType"),
      name: "customerType",
      value: filter.customerType,
      options: [
        { label: t("label.custTypeAll"), value: "" },
        { label: t("label.custTypeIndividual"), value: CustomerType.INDIVIDUAL },
        { label: t("label.custTypeCompany"), value: CustomerType.COMPANY },
      ],
    },
  ];

  const columns: Column[] = [
    {
      title: generateSortableTitle({
        title: t("label.customer").toUpperCase(),
        sortBy: "name",
      }),
      dataIndex: "name",
      width: "200px",
      render: (_: any, record: any) => {
        return <span className="link-style-view">{record.name || ""}</span>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            onViewDetails(record._id);
          },
        };
      },
    },
    {
      title: t("label.customerType").toUpperCase(),
      dataIndex: ["customerType"],
      width: "180px",
      render: (_: any, record: any) => {
        return <React.Fragment>{ToTitleCase(record.customerType)}</React.Fragment>;
      },
    },
    {
      title: t("label.country").toUpperCase(),
      dataIndex: ["primaryAddress"],
      render: (_: any, record: any) => {
        return isNil(record.primaryAddress) ? (
          <span>&#11835;</span>
        ) : (
          <span>
            {
              countryList.find((country) => {
                return country.Code === record.primaryAddress.country;
              })?.Name
            }
          </span>
        );
      },
    },
    {
      title: t("label.address").toUpperCase(),
      dataIndex: "primaryAddress",
      render: (_: any, record: any) => {
        const address = record.primaryAddress;
        return isNil(address) ? (
          <span>&#11835;</span>
        ) : (
          <span>
            {address.street1 + ", "}
            {address.street2 ? address.street2 + ", " : ""}
            {address.city + ", "}
            {address.state + ", "}
            {address.postcode + ", "}
            {countryList.find((country) => {
              return country.Code === address.country;
            })?.Name || ""}
          </span>
        );
      },
    },
    {
      title: t("label.contactNo").toUpperCase(),
      dataIndex: ["primaryPhone"],
      render: (_: any, record: any) => {
        const phone = record.primaryPhone;
        return <span>{`+${phone.countryCode} ${phone.number}`}</span>;
      },
    },
  ];

  if (showCompanyColumn) {
    columns.splice(1, 0, {
      title: generateSortableTitle({
        title: t("label.company").toUpperCase(),
        sortBy: "companyName",
      }),
      dataIndex: ["companyName"],
      render: (_: any, record: any) => {
        return isNil(record.companyName) ? (
          <span>&#11835;</span>
        ) : (
          <span>{record.companyName}</span>
        );
      },
    });
  }

  // allow user to access
  if (!isReadonly) {
    switch (tab) {
      case 1:
      case 3:
        columns.push({
          title: "",
          dataIndex: "actions",
          render: (_, record) => (
            <ActionMenu
              label={<MoreOutlined />}
              actions={[
                {
                  label: t("action.edit"),
                  icon: <EditOutlined />,
                  onClick: () => handleActionClick(record, "edit"),
                },
                {
                  label: t("action.delete"),
                  icon: <DeleteOutlined />,
                  onClick: () => handleActionClick(record, "delete"),
                },
              ]}
            />
          ),
        });
        break;
      case 2:
        columns.push({
          title: "",
          dataIndex: "actions",
          render: (_, record) => (
            <div className="btn-actions-container">
              <Button
                type="success"
                label={t("action.approve")}
                onClick={() => {
                  handleActionClick(record, "approve");
                }}
                className="approve-btn mb-5"
              />
              <Button
                type="danger"
                label={t("action.reject")}
                onClick={() => {
                  handleActionClick(record, "reject");
                }}
              />
            </div>
          ),
        });
        break;
      case 4:
        columns.push({
          title: "",
          dataIndex: "actions",
          render: (_, record) => (
            <div className="btn-actions-container">
              <Button
                type="success"
                label={t("action.approve")}
                onClick={() => {
                  handleActionClick(record, "approve");
                }}
                className="approve-btn mb-5"
              />
            </div>
          ),
        });
        break;
      default:
    }
  }
  // if (showCompanyListFilter) {
  //   dataTableFilters.unshift({
  //     key: "Filter2",
  //     label: t("label.filterByCompanies"),
  //     name: "companyOrgID",
  //     value: filter.companyOrgID,
  //     options: [{ label: t("label.companyAll"), value: "" }].concat(
  //       companyList.map((org) => {
  //         return {
  //           label: org.orgName || "",
  //           value: org.orgID,
  //         };
  //       })
  //     ),
  //     isReadonly: showLogisticProviderListFilter && filter.logisticProviderOrgID === "",
  //   });
  // }

  if (showLogisticProviderListFilter) {
    dataTableFilters.unshift({
      key: "Filter3",
      label: t("label.byLogisticProvider"),
      name: "logisticProviderOrgID",
      value: filter.logisticProviderOrgID,
      options: [{ label: t("label.logisticAll"), value: "" }].concat(
        logisticList.map((org) => {
          return {
            label: org.orgName || "",
            value: org.orgID,
          };
        }),
      ),
    });
  }

  const handleActionClick = (record, type) => {
    switch (type) {
      case "edit": {
        const customerID = encode(record._id);
        history.push(
          `/customer-management/profile/edit/${record.customerType?.toLowerCase()}/${customerID}`,
        );
        break;
      }
      case "delete": {
        setModal({ open: true, type: "delete", record });
        break;
      }
      case "reject": {
        setModal({ open: true, type: "reject", record });
        break;
      }
      case "approve": {
        setModal({ open: true, type: "approve", record });
        break;
      }
    }
  };

  const [operationalHourState, setOperationalHourState] = useState({
    dayFrom: "",
    dayTo: "",
    startTime: null,
    endTime: null,
  });

  const { dayFrom, dayTo, startTime, endTime } = operationalHourState;

  const handleOperationalHoursChange = (key: keyof typeof operationalHourState, value: any) => {
    setOperationalHourState((prevState) => ({ ...prevState, [key]: value }));
    setError(initialError);
  };

  const handleOperationalHoursSubmit = () => {
    const ids = list.map((customer) => {
      return customer._id;
    });

    let allowSubmit = true;

    if (operationalHourState.dayFrom == "") {
      setError({
        dayFrom: "Please choose a start day.",
      });
      allowSubmit = false;
    } else if (operationalHourState.dayTo == "") {
      setError({
        dayTo: "Please enter an end day.",
      });
      allowSubmit = false;
    } else if (operationalHourState.startTime == null) {
      setError({
        startTime: "Please enter a start time.",
      });
      allowSubmit = false;
    } else if (operationalHourState.endTime == null) {
      setError({
        endTime: "Please enter an end time.",
      });
      allowSubmit = false;
    }

    if (onSubmitOperationalHours && allowSubmit) {
      onSubmitOperationalHours(operationalHourState, ids);
      resetModalState();
    }
  };

  const ManageOperationalHours = () => {
    return (
      <div>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <h3>Primary Location Operational Hour</h3>
            <h4>For Individual Customer</h4>
          </Col>
          <Col span={24}>
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <SelectInput
                  isRequired={true}
                  label={"Day From"}
                  value={dayFrom}
                  options={[
                    { label: t("label.SUNDAY"), value: "SUNDAY" },
                    { label: t("label.MONDAY"), value: "MONDAY" },
                    { label: t("label.TUESDAY"), value: "TUESDAY" },
                    { label: t("label.WEDNESDAY"), value: "WEDNESDAY" },
                    { label: t("label.THURSDAY"), value: "THURSDAY" },
                    { label: t("label.FRIDAY"), value: "FRIDAY" },
                    { label: t("label.SATURDAY"), value: "SATURDAY" },
                  ]}
                  onChange={(value) => handleOperationalHoursChange("dayFrom", value)}
                />
                <AlertBox text={error.dayFrom} />
              </Col>
              <Col span={12}>
                <SelectInput
                  isRequired={true}
                  label={"Day To"}
                  value={dayTo}
                  options={[
                    { label: t("label.SUNDAY"), value: "SUNDAY" },
                    { label: t("label.MONDAY"), value: "MONDAY" },
                    { label: t("label.TUESDAY"), value: "TUESDAY" },
                    { label: t("label.WEDNESDAY"), value: "WEDNESDAY" },
                    { label: t("label.THURSDAY"), value: "THURSDAY" },
                    { label: t("label.FRIDAY"), value: "FRIDAY" },
                    { label: t("label.SATURDAY"), value: "SATURDAY" },
                  ]}
                  onChange={(value) => handleOperationalHoursChange("dayTo", value)}
                />
                <AlertBox text={error.dayTo} />
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col span={12}>
                <TimePicker
                  isRequired={true}
                  label={t("label.timeFrom")}
                  onChange={(value) => handleOperationalHoursChange("startTime", value)}
                  value={startTime}
                />
                <AlertBox text={error.startTime} />
              </Col>
              <Col span={12}>
                <TimePicker
                  isRequired={true}
                  label={t("label.timeTo")}
                  onChange={(value) => handleOperationalHoursChange("endTime", value)}
                  value={endTime}
                />
                <AlertBox text={error.endTime} />
              </Col>
            </Row>
            <Row>
              <Button
                type="primary"
                label={"Set"}
                onClick={() => {
                  handleOperationalHoursSubmit();
                }}
              />
              <Button
                type="danger"
                label={t("action.cancel")}
                className="btn--ghost"
                onClick={() => {
                  resetModalState();
                }}
              />
            </Row>
          </Col>
        </Row>
      </div>
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleSearch = async (text: string) => {
    setFilter((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleSubmit = (type: "APPROVED" | "REJECTED" | "DELETE") => {
    if (input.reason.length === 0 && (type === "REJECTED" || type === "DELETE")) {
      setError({
        ...error,
        reason: t("error.required", { field: t(`label.reason`) }),
      });
    } else {
      if (type === "DELETE") {
        onDeleteCustomer(modal.record._id, input.reason);
      } else {
        onSubmitApprovalStatus(
          omitBy(
            {
              approvalStatus: type,
              rejectedReason: input.reason,
              orgID: modal.record._id,
            },
            (value) => value === "",
          ) as CustomerApprovalInput,
          resetModalState(),
        );
      }
    }
  };

  const resetModalState = () => {
    setModal(initialModal);
    setInput(initialInput);
    setError(initialError);
    onFetchCustomerList();
  };

  return (
    <div className="customer-profile">
      <DataTable
        rowKey={(record: any) => {
          return record._id;
        }}
        columns={columns}
        dataSource={list}
        title={() => (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <DataTableFilter
              searchPlaceholder={t("label.searchByCustomerAddress")}
              onSearch={(text: string) => {
                handleSearch(text);
              }}
              onFilter={(filter) => {
                if (filter.logisticProviderOrgID === "") {
                  filter = { ...filter, companyOrgID: "" };
                }
                setFilter((prevFilter) => ({ ...prevFilter, ...filter }));
              }}
              renderFilterSelection={dataTableFilters}
            />
            {tab == 1 && filter.customerType == CustomerType.INDIVIDUAL ? (
              <a
                style={{ display: "flex", alignSelf: "end" }}
                onClick={() => {
                  setModal({ open: true, type: "manage" });
                }}
              >
                Manage individual operational hours here
              </a>
            ) : null}
          </div>
        )}
      />
      <BasicModal
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          resetModalState();
        }}
      >
        {modal.type === "approve" ? (
          <div className="customer-approval-modal">
            <div className="modal-title">{t("label.confirmCustomerApproval").toUpperCase()}</div>
            <div>{t("txt.areYouSureApproveAccount")}</div>
            <div>
              <b>{t("txt.customer").toUpperCase()}:</b> {modal.record?.name || ""}
            </div>
            <div className="mt-30 right">
              <Button
                type="success"
                label={t("action.confirm")}
                onClick={() => {
                  handleSubmit("APPROVED");
                }}
              />
              <Button
                type="success"
                label={t("action.cancel")}
                className="btn--ghost"
                onClick={() => {
                  resetModalState();
                }}
              />
            </div>
          </div>
        ) : modal.type !== "manage" ? (
          <div className="customer-reject-modal">
            <div className="modal-title">
              {modal.type === "delete"
                ? t("label.deleteCustomer").toUpperCase()
                : t("label.rejectCustomer").toUpperCase()}
            </div>
            <div>
              {modal.type === "delete"
                ? t("txt.areYouSureDeleteAccount")
                : t("txt.areYouSureRejectAccount")}
            </div>
            <div>
              <b>{t("txt.customer").toUpperCase()}:</b> {modal.record?.name || ""}
            </div>
            <div>
              <label className="inp-label inp-label--required">{t("label.enterReason")}</label>
              <Input.TextArea
                id="message"
                size="middle"
                rows={3}
                name="reason"
                value={input.reason}
                onChange={handleChange}
                className="mt-5"
                required={true}
                placeholder={t("label.enterHere")}
              />
              <AlertBox text={error.reason} />
            </div>
            <div className="mt-30 right">
              <Button
                type="danger"
                label={modal.type === "delete" ? t("action.delete") : t("action.reject")}
                onClick={() => {
                  handleSubmit(modal.type === "delete" ? "DELETE" : "REJECTED");
                }}
              />
              <Button
                type="danger"
                label={t("action.cancel")}
                className="btn--ghost"
                onClick={() => {
                  resetModalState();
                }}
              />
            </div>
          </div>
        ) : (
          <ManageOperationalHours />
        )}
      </BasicModal>
    </div>
  );
};

export default CustomerProfileListComponent;
